/**
 * SessionLifecycle — sticky toolbar at the top of the Chat tab.
 *
 * The right button depends on the session state (per SESSION_RUNTIME.md
 * §3.2). All buttons use neutral chrome — primary = `bg-foreground
 * text-background`, ghost = transparent with hover wash. No coloured
 * pills, no shadow.
 *
 * Server-load gate (Phase 12 spec §B): when `serverLoad` is provided,
 *   - load >= 90% → Start disabled + tooltip "Server at capacity"
 *   - load >= 70% → "Server is busy" caption alongside Start (still enabled)
 *
 * The pause/end actions are not gated — the founder can always reclaim
 * resources.
 */
import type { SessionState } from '@chaos/types/session';
import { MoreHorizontal, Pause, Play, Power, RotateCcw } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface SessionLifecycleProps {
  state: SessionState;
  pending: 'start' | 'pause' | 'resume' | 'end' | null;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onEnd: () => void;
  /** Aggregate RAM/CPU pct (0..100), if known. */
  loadPct?: number | null;
  className?: string;
}

const HIGH_LOAD_PCT = 90;
const BUSY_LOAD_PCT = 70;

export function SessionLifecycle({
  state,
  pending,
  onStart,
  onPause,
  onResume,
  onEnd,
  loadPct,
  className,
}: SessionLifecycleProps) {
  const overloaded = typeof loadPct === 'number' && loadPct >= HIGH_LOAD_PCT;
  const busy = typeof loadPct === 'number' && loadPct >= BUSY_LOAD_PCT && !overloaded;

  return (
    <TooltipProvider delayDuration={250}>
      <div
        role="toolbar"
        className={cn(
          'flex items-center justify-between gap-2 border-b border-border bg-background/80 px-1 py-1.5',
          className,
        )}
        aria-label="Session controls"
      >
        <div className="flex items-center gap-2">
          {state === 'created' && (
            <StartButton
              pending={pending === 'start'}
              disabled={overloaded}
              onClick={onStart}
              overloaded={overloaded}
              busy={busy}
            />
          )}

          {(state === 'active' || state === 'idle') && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onPause}
              disabled={pending !== null}
              aria-label="Pause session"
            >
              <Pause className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {pending === 'pause' ? 'Pausing…' : 'Pause session'}
            </Button>
          )}

          {state === 'sleeping' && (
            <Button
              size="sm"
              variant="default"
              onClick={onResume}
              disabled={pending !== null || overloaded}
              aria-label="Resume session"
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {pending === 'resume' ? 'Resuming…' : 'Resume session'}
            </Button>
          )}

          {state === 'waking' && (
            <Button size="sm" variant="ghost" disabled aria-label="Session resuming">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              Resuming…
            </Button>
          )}

          {state === 'dead' && (
            <Button
              size="sm"
              variant="default"
              onClick={onStart}
              disabled={pending !== null || overloaded}
              aria-label="Start a fresh session"
            >
              <Play className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {pending === 'start' ? 'Starting…' : 'Start session'}
            </Button>
          )}

          {state === 'error' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onStart}
              disabled={pending !== null}
              aria-label="Retry session"
              className="text-error"
            >
              Retry session
            </Button>
          )}

          {busy && (state === 'created' || state === 'sleeping') && (
            <span className="text-[11px] text-muted-foreground" role="status">
              <span aria-hidden>⚠ </span>Server is busy
            </span>
          )}
        </div>

        {/* End-session kebab — only shown when there's something to end. */}
        {state !== 'created' && state !== 'dead' && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                aria-label="More session options"
                className="h-7 w-7"
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={onEnd}
                disabled={pending !== null}
                className="text-error focus:text-error"
              >
                <Power className="h-4 w-4" aria-hidden />
                <span>End session permanently</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </TooltipProvider>
  );
}

function StartButton({
  pending,
  disabled,
  onClick,
  overloaded,
  busy,
}: {
  pending: boolean;
  disabled: boolean;
  onClick: () => void;
  overloaded: boolean;
  busy: boolean;
}) {
  const button = (
    <Button
      size="sm"
      variant="default"
      onClick={onClick}
      disabled={disabled || pending}
      aria-label="Start session"
    >
      <Play className="mr-1.5 h-3.5 w-3.5" aria-hidden />
      {pending ? 'Starting…' : 'Start session'}
    </Button>
  );
  if (!overloaded && !busy) return button;
  if (overloaded) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Wrap in a span so the disabled button still triggers the tooltip. */}
          <span>{button}</span>
        </TooltipTrigger>
        <TooltipContent>Server at capacity — pause another session first.</TooltipContent>
      </Tooltip>
    );
  }
  return button;
}
